import {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useAuth} from '../../services/use-auth';
import usePageMeta from '../../services/page-meta';
import NotFoundPage from '../Errors/NotFound';
import {parseRouteResponse} from '../../services/route-response';
import {CertificateCard, MainDashboardSkeleton} from './index';
import './dashboard.scss';

function resolveCertificateYear(item) {
    const sortTimestamp = Number(item?.sortTimestamp ?? 0);

    if (Number.isFinite(sortTimestamp) && sortTimestamp > 0) {
        const timestamp = sortTimestamp > 9999999999 ? sortTimestamp : sortTimestamp * 1000;
        const date = new Date(timestamp);

        if (!Number.isNaN(date.getTime())) {
            return String(date.getFullYear());
        }
    }

    const fallbackDate = String(item?.dateFinished || item?.completedAt || '');
    const match = fallbackDate.match(/\b(19|20)\d{2}\b/);

    return match ? match[0] : null;
}

export default function MainCertificatesPage() {
    const {t} = useTranslation();
    usePageMeta({
        title: t('main.dashboard.certificates.title', 'Certificates'),
        description: t('meta.main.description', t('app.description')),
        keywords: t('meta.main.keywords', t('app.keywords')),
    });

    const auth = useAuth();
    const [payload, setPayload] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [notFound, setNotFound] = useState(false);
    const [page, setPage] = useState(1);
    const [selectedYear, setSelectedYear] = useState('all');

    useEffect(() => {
        if (!auth?.user) {
            setPayload(null);
            setNotFound(false);
            setIsLoading(false);
            return;
        }

        let active = true;
        setIsLoading(true);

        fetch('/api/v1/main/certificates', {credentials: 'same-origin'})
            .then(async (response) => {
                const result = await response.json().catch(() => null);
                const {kind, redirectUrl} = parseRouteResponse(response, result);

                if (kind === 'not_found') {
                    if (active) {
                        setNotFound(true);
                    }

                    return null;
                }

                if ((kind === 'unauthorized' || kind === 'redirect' || kind === 'error') && redirectUrl) {
                    window.location.href = redirectUrl;
                    return null;
                }

                if (kind === 'unauthorized') {
                    return null;
                }

                if (kind === 'error') {
                    throw new Error('Cannot load certificates page');
                }

                if (active) {
                    setNotFound(false);
                }

                return result;
            })
            .then((result) => {
                if (!active) {
                    return;
                }

                setPayload(result);
                setPage(1);
            })
            .catch(() => {
                if (!active) {
                    return;
                }

                setPayload(null);
            })
            .finally(() => {
                if (!active) {
                    return;
                }

                setIsLoading(false);
            });

        return () => {
            active = false;
        };
    }, [auth?.user]);

    const items = useMemo(() => (Array.isArray(payload?.items) ? payload.items : []), [payload]);
    const availableYears = useMemo(() => {
        const years = new Set();

        items.forEach((item) => {
            const year = resolveCertificateYear(item);

            if (year) {
                years.add(year);
            }
        });

        return Array.from(years).sort((left, right) => Number(right) - Number(left));
    }, [items]);
    const filteredItems = useMemo(() => {
        if (selectedYear === 'all') {
            return items;
        }

        return items.filter((item) => resolveCertificateYear(item) === selectedYear);
    }, [items, selectedYear]);

    useEffect(() => {
        if (selectedYear !== 'all' && !availableYears.includes(selectedYear)) {
            setSelectedYear('all');
        }
    }, [availableYears, selectedYear]);

    useEffect(() => {
        setPage(1);
    }, [selectedYear]);

    if (!auth?.user) {
        return null;
    }

    if (notFound) {
        return <NotFoundPage />;
    }

    if (isLoading && !payload) {
        return <MainDashboardSkeleton />;
    }

    if (!payload) {
        return (
            <section className="main-dashboard">
                <div className="container">
                    <div className="main-dashboard__shell">
                        <div className="main-dashboard__status">
                            {t('main.dashboard.unavailable', 'Dashboard is temporarily unavailable.')}
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    const perPage = 6;
    const totalPages = Math.max(1, Math.ceil(filteredItems.length / perPage));
    const currentPage = Math.min(page, totalPages);
    const visibleItems = filteredItems.slice((currentPage - 1) * perPage, currentPage * perPage);

    return (
        <section className="main-dashboard">
            <div className="container">
                <div className="main-dashboard__shell">
                    <div className="main-dashboard__section">
                        <div className="main-dashboard__section-head">
                            <div>
                                <h2>{t('main.dashboard.certificates.title', 'Certificates')}</h2>
                                <p>{t('main.dashboard.certificates.subtitle', 'Completed courses with quick certificate access.')}</p>
                            </div>
                            {availableYears.length > 0 && (
                                <div className="main-dashboard__section-actions">
                                    <label className="main-dashboard__filter" htmlFor="main-certificates-year">
                                        <span className="main-dashboard__filter-label">
                                            {t('main.dashboard.certificates.filters.label', 'Year')}
                                        </span>
                                        <div className="main-dashboard__filter-control">
                                            <select
                                                id="main-certificates-year"
                                                className="main-dashboard__filter-select"
                                                value={selectedYear}
                                                onChange={(event) => setSelectedYear(event.target.value)}
                                            >
                                                <option value="all">
                                                    {t('main.dashboard.certificates.filters.allYears', 'All years')}
                                                </option>
                                                {availableYears.map((year) => (
                                                    <option value={year} key={year}>{year}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </label>
                                </div>
                            )}
                        </div>

                        {filteredItems.length > 0 ? (
                            <>
                                <div className="main-dashboard__cards-grid">
                                    {visibleItems.map((item) => (
                                        <CertificateCard item={item} t={t} key={`main-certificate-${item.type}-${item.id}`} />
                                    ))}
                                </div>
                                {totalPages > 1 && (
                                    <div className="main-dashboard__section-actions main-dashboard__cards-pagination">
                                        <button
                                            type="button"
                                            className="main-dashboard__nav"
                                            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                                            disabled={currentPage <= 1}
                                            aria-label={t('main.dashboard.actions.previous', 'Previous')}
                                        >
                                            {'<'}
                                        </button>
                                        <span className="main-dashboard__updates-page">
                                            {currentPage} / {totalPages}
                                        </span>
                                        <button
                                            type="button"
                                            className="main-dashboard__nav"
                                            onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                                            disabled={currentPage >= totalPages}
                                            aria-label={t('main.dashboard.actions.next', 'Next')}
                                        >
                                            {'>'}
                                        </button>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="main-dashboard__status">
                                {selectedYear === 'all'
                                    ? t('main.dashboard.certificates.empty', 'No certificates yet.')
                                    : t('main.dashboard.certificates.emptyFiltered', 'No certificates for the selected year.')}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}
